/* The array on the water, as geometry.
 *
 * Shared by the staff editor that draws the farm and by the customer page that
 * colours it, and that sharing is the whole reason it is a module rather than
 * two loops inside two components. A ring drawn 25 m across on one screen and
 * 51 m across on the other is not a styling difference — it is two different
 * claims about how tightly the array is packed, which is the one thing a
 * picture of a fish farm is actually for.
 *
 * THERE IS NO RADIUS ANYWHERE IN THIS FILE'S INPUTS. `circumference_m` is
 * already on the pen, already filled in at onboarding, and is the number the
 * industry speaks: a farm has 160-ringer, nobody says "radius 25,5 m". The
 * footprint is derived from it, so the polygon and the volume a delousing is
 * priced against are one fact rendered twice. A stored radius would be a second
 * copy of one measurement, and the two copies disagree the first time somebody
 * re-rings a cage and updates one of them.
 */
import type { Pen, PenShape, Structure } from './api'

/** A 160-ring, because that is what most of the coast is.
 *
 *  Used only when nobody has told us the size. The map says which by drawing
 *  an assumed footprint faintly — a missing measurement shown as a guess, not
 *  a guess shown as a fact. */
export const DEFAULT_CIRCUMFERENCE_M = 160

/** A feed barge nobody has measured. Roughly a Marine Harvest–size flåte. */
export const DEFAULT_BARGE_M = { length: 30, width: 12 }

const M_PER_DEG_LAT = 111320

/** Metres east and north, from a point, in degrees.
 *
 *  A local flat-earth approximation, which is exact enough at the scale of one
 *  farm — the error over 500 m is under a centimetre — and which avoids
 *  putting a projection library in the bundle for eight polygons. It is wrong
 *  at fleet scale, and nothing here is ever asked to draw a fleet. */
export function offset(lat: number, lon: number, eastM: number, northM: number)
: [number, number] {
  const dLat = northM / M_PER_DEG_LAT
  const dLon = eastM / (M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180))
  return [lon + dLon, lat + dLat]
}

/** Rotate a local (east, north) offset clockwise from north.
 *
 *  Clockwise, because that is what a bearing is: 90° is east. Getting the sign
 *  wrong mirrors the array, which looks plausible and is wrong in the one
 *  direction that matters — which side of the cages the current arrives on. */
export function turn(eastM: number, northM: number, deg: number)
: [number, number] {
  const r = (deg * Math.PI) / 180
  const c = Math.cos(r), s = Math.sin(r)
  return [eastM * c + northM * s, -eastM * s + northM * c]
}

export function radiusM(circumference: number | null | undefined): number {
  return (circumference ?? DEFAULT_CIRCUMFERENCE_M) / (2 * Math.PI)
}

export function sideM(circumference: number | null | undefined): number {
  return (circumference ?? DEFAULT_CIRCUMFERENCE_M) / 4
}

/** The footprint of one ring, as a closed GeoJSON ring of [lon, lat]. */
export function penRing(
  lat: number, lon: number, shape: PenShape,
  circumference: number | null | undefined, bearing: number | null | undefined,
): [number, number][] {
  if (shape === 'square') {
    const h = sideM(circumference) / 2
    const b = bearing ?? 0
    const corners: [number, number][] = [
      [-h, h], [h, h], [h, -h], [-h, -h],
    ]
    const pts = corners
      .map(([e, n]) => turn(e, n, b))
      .map(([e, n]) => offset(lat, lon, e, n))
    return [...pts, pts[0]!]
  }
  // 48 segments. Fewer and a 50-metre ring reads as a stop sign at the zoom
  // somebody actually places them at.
  const r = radiusM(circumference)
  const pts: [number, number][] = []
  for (let i = 0; i <= 48; i++) {
    const a = (i / 48) * 2 * Math.PI
    pts.push(offset(lat, lon, r * Math.sin(a), r * Math.cos(a)))
  }
  return pts
}

export function structureRing(
  lat: number, lon: number, lengthM: number | null | undefined,
  widthM: number | null | undefined, bearing: number | null | undefined,
): [number, number][] {
  const l = (lengthM ?? DEFAULT_BARGE_M.length) / 2
  const w = (widthM ?? DEFAULT_BARGE_M.width) / 2
  const b = bearing ?? 0
  const pts = ([[-w, l], [w, l], [w, -l], [-w, -l]] as [number, number][])
    .map(([e, n]) => turn(e, n, b))
    .map(([e, n]) => offset(lat, lon, e, n))
  return [...pts, pts[0]!]
}

export interface DrawnFeature {
  type: 'Feature'
  id?: string | number
  properties: Record<string, unknown>
  geometry: { type: 'Polygon'; coordinates: [number, number][][] }
}

/** One pen as a drawable feature, or null if nobody has placed it yet.
 *
 *  Null rather than a position invented at the site centre. Eight unplaced
 *  rings stacked on one another is a picture that says the farm is drawn when
 *  it is not, and the caller can count the nulls and say so in words. */
export function penFeature(pen: Pen, props: Record<string, unknown> = {})
: DrawnFeature | null {
  if (pen.lat === null || pen.lat === undefined
      || pen.lon === null || pen.lon === undefined) return null
  return {
    type: 'Feature',
    id: pen.pen_id,
    properties: {
      kind: 'pen', pen_no: pen.pen_no,
      // So the caller can draw an assumed footprint differently from a
      // measured one without re-deriving why it is assumed.
      assumed: pen.circumference_m === null || pen.circumference_m === undefined,
      ...props,
    },
    geometry: {
      type: 'Polygon',
      coordinates: [penRing(pen.lat, pen.lon, pen.shape ?? 'circle',
                            pen.circumference_m, pen.bearing_deg)],
    },
  }
}

export function structureFeature(s: Structure,
                                 props: Record<string, unknown> = {})
: DrawnFeature {
  return {
    type: 'Feature',
    id: s.structure_id,
    properties: { kind: 'structure', structure: s.kind, ...props },
    geometry: {
      type: 'Polygon',
      coordinates: [structureRing(s.lat, s.lon, s.length_m, s.width_m,
                                  s.bearing_deg)],
    },
  }
}

/** Positions for rings nobody has placed, laid out as a farm actually is.
 *
 *  Two rows, because that is how a bracket-moored array is built and because a
 *  single row of eight is 400 metres long and leaves the reader scrolling. The
 *  spacing is 1,8 ring diameters, which is roughly the real gap.
 *
 *  This exists because the alternative — an empty map and eight rings in a
 *  tray — makes the first minute of the editor the worst one. Seeded positions
 *  are wrong in a way that is obvious and fixable by dragging; an empty map is
 *  a blank page. Nothing here is saved until somebody presses save.
 */
export function seedPositions(
  n: number, lat: number, lon: number,
  circumference: number | null | undefined, bearing = 0,
): { lat: number; lon: number }[] {
  const step = radiusM(circumference) * 2 * 1.8
  const cols = Math.ceil(n / 2)
  const rows = n > 1 ? 2 : 1
  const out: { lat: number; lon: number }[] = []
  for (let i = 0; i < n; i++) {
    const col = i % cols
    const row = Math.floor(i / cols)
    const east = (col - (cols - 1) / 2) * step
    const north = ((rows - 1) / 2 - row) * step
    const [e, nn] = turn(east, north, bearing)
    const [lo, la] = offset(lat, lon, e, nn)
    out.push({ lat: la, lon: lo })
  }
  return out
}


/* ---------------------------------------------------------- moving a group

   THE ARRAY MOVES AS ONE THING, and that is not a convenience.

   A farm's rings are laid out relative to each other -- the bracket spacing,
   the row offset, which end points into the current -- and every one of those
   relationships is right the moment the first ring is right. Dragging eight
   rings one at a time destroys all of them and then asks somebody to rebuild
   them by eye. What is actually wrong after an automatic layout is the array's
   position, its orientation and its spacing: three numbers, not sixteen.

   So these three functions, and they are the whole of the group edit. They work
   in metres east and north of a centroid rather than in degrees, because a
   rotation in degrees of latitude and longitude is a shear -- at 60 °N a
   degree of longitude is half a degree of latitude, and an array rotated that
   way comes out as a parallelogram. */

export interface Point { lat: number; lon: number }

/** Metres east and north of a centre. The inverse of `offset`. */
export function toLocal(c: Point, p: Point): [number, number] {
  const north = (p.lat - c.lat) * M_PER_DEG_LAT
  const east = (p.lon - c.lon) * M_PER_DEG_LAT
    * Math.cos((c.lat * Math.PI) / 180)
  return [east, north]
}

export function centroid(pts: Point[]): Point | null {
  if (pts.length === 0) return null
  return {
    lat: pts.reduce((s, p) => s + p.lat, 0) / pts.length,
    lon: pts.reduce((s, p) => s + p.lon, 0) / pts.length,
  }
}

/** Turn one point about a centre, clockwise, in degrees. */
export function rotateAbout(c: Point, p: Point, deg: number): Point {
  const [e, n] = turn(...toLocal(c, p), deg)
  const [lon, lat] = offset(c.lat, c.lon, e, n)
  return { lat, lon }
}

/** Push a point away from a centre, or pull it in. Spacing, not size: the
 *  rings keep their own diameter and only the gaps between them change. */
export function spreadAbout(c: Point, p: Point, factor: number): Point {
  const [e, n] = toLocal(c, p)
  const [lon, lat] = offset(c.lat, c.lon, e * factor, n * factor)
  return { lat, lon }
}


/** Which ring a point landed in, or null.
 *
 *  Used to resolve a dropped fish badge to a merd. Nearest centre, and only
 *  when the drop is inside a generous version of the ring — 1,5 radii, because
 *  a finger on a phone is about 8 mm across and the ring drawn on screen may be
 *  30 pixels. Anything looser and a slip becomes a move of somebody's fish;
 *  anything tighter and the gesture fails for people wearing gloves, which is
 *  everyone this is for.
 *
 *  Null is a real answer and means "put it back": a drop on open water is not
 *  a move, and guessing the closest ring from 200 metres away would file an
 *  operation nobody performed.
 */
export function penAt<T extends { lat?: number | null; lon?: number | null
                                  circumference_m?: number | null }>(
  pens: T[], p: Point,
): T | null {
  let best: T | null = null
  let bestM = Infinity
  for (const pen of pens) {
    if (pen.lat == null || pen.lon == null) continue
    const [e, n] = toLocal({ lat: pen.lat, lon: pen.lon }, p)
    const d = Math.hypot(e, n)
    // Two radii, not one. A finger is about 8 mm across and a ring may draw
    // at 30 pixels, so a tolerance the size of the ring itself fails for
    // exactly the people this is for. Two radii is still well inside the gap
    // between neighbouring rings, which is about 1,8 diameters.
    if (d < bestM && d <= radiusM(pen.circumference_m) * 2) {
      best = pen
      bestM = d
    }
  }
  return best
}

/* ------------------------------------------------------------------ strøm */

/** One chevron, as a closed ring of [lon, lat].
 *
 *  A dart and not a line-with-a-head: MapLibre lines have no arrowhead, and a
 *  `symbol` layer with `icon-rotate` draws a fixed-pixel glyph that stops
 *  scaling with the ring it belongs to — at the zoom where a cage is fifty
 *  pixels across, a fixed arrowhead is no longer beside it but on top of it.
 *
 *  Built in metres pointing north and rotated with `turn`, so the shape is
 *  defined once and the bearing does the rest. The notch at the back is what
 *  makes it read as a chevron rather than a triangle, and a chevron is what
 *  makes a row of them read as movement. */
export function chevronRing(
  lat: number, lon: number, bearing: number, atM: number, sizeM: number,
): [number, number][] {
  const h = sizeM
  const w = sizeM * 0.74

  const pts = ([
    [0, atM + h],          // tip
    [-w, atM],             // left wing
    [0, atM + h * 0.30],   // the notch
    [w, atM],              // right wing
  ] as [number, number][])
    .map(([e, n]) => turn(e, n, bearing))
    .map(([e, n]) => offset(lat, lon, e, n))

  return [...pts, pts[0]!]
}

/** How many chevrons a band of water exchange draws.
 *
 *  Speed is already in the length of the trail; the count says it again in a
 *  channel that survives being glanced at. One chevron is a direction, three
 *  is a current. `strong` does not get four: the scale is not "more is better"
 *  — past a point the net deforms and the fish swim to hold station — so the
 *  trail stops growing and the colour carries the caution instead. */
export function chevronCount(exchange: string | null | undefined): number {
  // Two, never three. A 90 m array leaves about 64 m between a ring's centre
  // and its neighbour's rim, and three chevrons big enough to read at working
  // zoom do not fit in it. Two that can be seen beat three that cannot.
  return (exchange === 'good' || exchange === 'strong') ? 2 : 1
}

/** Where the water arrives at a ring, and where it leaves it.
 *
 *  Both trails point the same way — downstream — because that is what the
 *  water does. The upstream one sits on the side the current arrives from and
 *  runs INTO the ring; the downstream one sits on the far side and runs away.
 *  Two trails rather than one because one beside a ring is ambiguous about
 *  whether it describes that ring or the gap next to it, and because a farm
 *  manager reads the pair as "through this cage", which is the actual claim.
 *
 *  `lengthR` arrives from the service in multiples of the radius, so the scale
 *  that decides how far a trail runs lives with the forecast that justifies it
 *  rather than being reinvented here. */
export function flowArrows(
  lat: number, lon: number, circumference: number | null | undefined,
  bearingToward: number, standoffR: number, lengthR: number,
  count = 1,
): { upstream: [number, number][][]; downstream: [number, number][][] } {
  const r = radiusM(circumference)
  const n = Math.max(1, Math.min(2, count))

  /* Sized to be SEEN. The first version scaled the chevron down to fit three
     of them in the gap and produced a row of specks: at working zoom a ring is
     about fifty pixels across, and a mark a tenth of that is not a direction
     anybody can read. Presence first, then the count. */
  const fast = Math.max(0, Math.min(1, (lengthR - 0.45) / 1.15))
  const size = r * (0.50 + 0.16 * fast)
  const step = size * 1.15
  const from = r * standoffR

  const upstream: [number, number][][] = []
  const downstream: [number, number][][] = []

  for (let i = 0; i < n; i++) {
    // Leading chevron largest, tapering back along the trail — the shape a
    // wake has, and the reason a static row of them reads as moving.
    const scale = 1 - (n - 1 - i) * 0.28
    const at = from + i * step

    downstream.push(chevronRing(lat, lon, bearingToward, at, size * scale))
    // Mirrored onto the upstream side and still pointing downstream: the water
    // is arriving here, not leaving.
    upstream.push(chevronRing(lat, lon, bearingToward,
                              -(at + size * scale), size * scale))
  }

  return { upstream, downstream }
}

/** Slack water: a small ring beside the cage rather than an arrow.
 *
 *  Drawn when the forecast says the water is barely moving. A short arrow and
 *  no arrow are both wrong — the first claims a direction the model cannot
 *  support at that speed, the second is indistinguishable from a forecast that
 *  failed to load. A small circle says "we asked, and there is nothing to
 *  point at", which is a third thing and the true one.
 *
 *  It sits due north of the ring, in the same place for every cage and every
 *  slack hour. Deliberately not on the last known bearing: that would put a
 *  direction back into the one mark whose entire meaning is that there is no
 *  direction worth drawing. Fixed and repeated, it reads as a state. */
export function slackRing(
  lat: number, lon: number, circumference: number | null | undefined,
  standoffR: number,
): [number, number][] {
  const r = radiusM(circumference)
  const at = r * standoffR
  const size = Math.max(r * 0.16, 2.5)
  const pts: [number, number][] = []
  for (let i = 0; i <= 20; i++) {
    const a = (i / 20) * 2 * Math.PI
    pts.push(offset(lat, lon, size * Math.sin(a), at + size * Math.cos(a)))
  }
  return pts
}
